/** @format */

import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { fetchFlags } from "./featureFlagAPI";

/** Define types for FeatureFlag and State */
export interface FeatureFlag {
  name: string;
  isActive: boolean;
}
export interface FeatureFlagState {
  flags: FeatureFlag[];
  status: "idle" | "loading" | "failed" | "notloaded";
}

/** Define the initial state */
export const initialState: FeatureFlagState = {
  flags: [],
  status: "notloaded",
};

/**
 * Thunk actions (i.e. Delayed actions)
 * @see https://redux.js.org/usage/writing-logic-thunks#what-is-a-thunk
 *
 * createAsyncThunk()
 * @see https://redux-toolkit.js.org/api/createAsyncThunk
 * */

/** Action to load the flags into the sate. */
export const loadFlags = createAsyncThunk(
  "featureFlag/fetchFlags",
  /** Fetch the flags using the featureFlagAPI and return the data. */
  async (): Promise<FeatureFlag[]> => {
    const response = await fetchFlags();
    return response.data;
  },
);

/**
 * Slice / Reducers
 * @see https://redux-toolkit.js.org/api/createSlice
 */
export const featureFlagSlice = createSlice({
  name: "featureFlag",
  initialState,
  reducers: {
    /** Reducer function to set add or edit a single flag in the state */
    setFlag: (state, action: PayloadAction<FeatureFlag>) => {
      const flagIndex = state.flags.findIndex(
        (flag) => flag?.name === action.payload.name,
      );
      if (flagIndex !== -1) state.flags.splice(flagIndex, 1);
      state.flags.push(action.payload);
    },

    /** Reducer function to remove a single flag in the state */
    removeFlag: (state, action: PayloadAction<string>) => {
      const flagIndex = state.flags.findIndex(
        (flag) => flag?.name === action.payload,
      );
      if (flagIndex !== -1) state.flags.splice(flagIndex, 1);
    },
  },

  /** Extra reducer functions for the Thunks above to handle loading, fulfilled or rejected promises. */
  extraReducers: (builder) => {
    builder
      .addCase(loadFlags.pending, (state) => {
        state.status = "loading";
      })
      .addCase(loadFlags.fulfilled, (state, action) => {
        state.status = "idle";
        state.flags = action.payload;
      });
    // .addCase(loadFlags.rejected, (state) => {
    //   state.status = 'failed'
    //   state.flags = []
    // })
  },
});

/**
 * Simple Actions (not including thunks which are already exported above).
 *
 * These are used with dispatchers to edit the state. e.g. dispatch(setFlag({name: flagname, isActive: true}))
 *
 * The Slice automatically creates the actions from the reducers, we just need to export them.
 */
export const { setFlag, removeFlag } = featureFlagSlice.actions;

/**
 * Selectors.
 *
 * Used with selector to fetch data from the state. e.g. selector(selectStatus())
 */
export const selectStatus = () => (state: RootState) =>
  state.featureFlag.status;
export const selectFlags = () => (state: RootState) => state.featureFlag.flags;
export const selectFlag = (flagName: string) => (state: RootState) =>
  state.featureFlag.flags.find((flag) => flag?.name === flagName);
export const selectFlagValue = (flagName: string) => (state: RootState) =>
  state.featureFlag.flags.find((flag) => flag?.name === flagName)?.isActive;
export const isOneOfTheseFlagsTrue =
  (flagNames: string[]) => (state: RootState) => {
    for (let i = 0; i < flagNames.length; i++) {
      if (
        state.featureFlag?.flags.find((flag) => flag?.name === flagNames[i])
          ?.isActive
      )
        return true;
    }
    return false;
  };

/**
 * Export the main reducer from the Slice.
 */
export default featureFlagSlice.reducer;
